import { useQuery } from '@apollo/client';
import { AccordionPanel, Board, Panel } from '@gamma/ui/layout';
import { ErrorBoundary } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useMemo } from 'react';
import { graphqlErrorRedirects } from '@investigator/shared/constants';
import {
  useLogOperationCall,
  useUserSession,
} from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { GET_LICENSE } from '../../queries';

import { GetLicenseInfoQuery } from '@investigator/shared/generated/graphql';
import { LicenseInformation } from './Components/LicenseInformation/LicenseInformation';
import { LicenseOverview } from './Components/LicenseOverview/LicenseOverview';
import { expirationDatesLicenseCompare } from '@investigator/shared/utils';

export const LicenseDashboard = () => {
  const { signOut } = useUserSession();

  const logOperationCall = useLogOperationCall();
  const { loading, error, data } = useQuery<GetLicenseInfoQuery>(GET_LICENSE, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      logOperationCall('LICENSE_VIEWED', 'ADMIN_USER_ACTIONS');
    },
  });

  const { title } = i18n.pages.licenseDashboard;
  const {
    end = 0,
    license_type = '',
    expired = false,
  } = data?.getLicenseInfo ?? {};

  const expirationMeta = useMemo(
    () =>
      expirationDatesLicenseCompare(
        Number(end),
        license_type,
        Boolean(expired),
      ),
    [end, license_type, expired],
  );

  if (loading || error) {
    return (
      <Panel>
        <GraphQLReqStatus
          loading={loading}
          error={error}
          signOut={signOut}
          extended={graphqlErrorRedirects}
        />
      </Panel>
    );
  }

  return (
    <ErrorBoundary page="View License" styleClass="main">
      <AccordionPanel
        title={title}
        reduceMotion={true}
        storageKey="license-information-panel"
        data-testid="license-information-page"
      >
        <Board>
          <LicenseOverview expirationMeta={expirationMeta} data={data} />
          <LicenseInformation data={data} />
        </Board>
      </AccordionPanel>
    </ErrorBoundary>
  );
};
